import * as _ from "lodash-es";
import React, { ChangeEvent, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import IErrorDTO from "../../../../models/DTO/IErrorDTO";
import IGetVendorsDTO from "../../../../models/DTO/IGetVendorsDTO";
import IOnGetVendorsDTO from "../../../../models/DTO/IOnGetVendorsDTO";
import EndpointsEnum from "../../../../models/EndpointsEnum";
import ISupplierModel from "../../../../models/ISupplierModel";
import IUserUI from "../../../../models/IUserUI";
import IVendorModel from "../../../../models/IVendorModel";
import UIService from "../../../../services/UIService";
import ChevronToggle from "../../../shared/ChevronToggle/ChevronToggle";
import OwButton from "../../../shared/OwButton/OwButton";
import OwCheckbox from "../../../shared/OwCheckbox/OwCheckbox";
import OwInputWrapper from "../../../shared/OwInputWrapper/OwInputWrapper";
import OwLabel from "../../../shared/OwLabel/OwLabel";
import OwSelectOption from "../../../shared/OwSelectOption/OwSelectOption";
import OwTitle from "../../../shared/OwTitle/OwTitle";
import Typeahead from "../../../shared/Typeahead/Typeahead";
import { Socket } from "socket.io-client";

type Props = {
    userModel: IUserUI;
    supplierModel: ISupplierModel;
    socket: Socket;
    setActivityIndicator: (activityOn: boolean) => void;
    notifyFn: (data: IErrorDTO | null, _notification?: any, isFormatted?: boolean) => void;
    searchFn: (queryString?: string) => void;
};

export const PackingListSearch: React.FC<Props> = (props) => {
    const getInitialFormData = (): {
        vendor: string;
        pick_list: string;
        packing_list: string;
        order: string;
        locked: boolean | undefined;
    } => {
        return {
            vendor: "",
            pick_list: "",
            packing_list: "",
            order: "",
            locked: undefined,
        };
    };

    const initialFormData = getInitialFormData();

    const history = useNavigate();
    const location = useLocation();

    const params = new URLSearchParams(location.search);

    const pick_list_name = params.get("pick_list_name");
    const order_name = params.get("order_name");
    const packing_list_name = params.get("packing_list_name");
    const locked = params.get("locked");
    const vendor_name = params.get("vendor_name");

    const [query, setQuery] = useState("");

    if (pick_list_name) {
        initialFormData.pick_list = pick_list_name;
    }

    if (order_name) {
        initialFormData.order = order_name;
    }

    if (packing_list_name) {
        initialFormData.packing_list = packing_list_name;
    }

    if (locked) {
        if (locked === "true" || locked) {
            initialFormData.locked = true;
        } else if (locked === "false") {
            initialFormData.locked = false;
        }
    }

    if (vendor_name) {
        initialFormData.vendor = vendor_name;
    }

    const [initSearch, setInitSearch] = useState(true);
    const [isVisible, setIsVisible] = useState(true);
    const [formData, setFormData] = useState(initialFormData);
    const [selectedVendor, setSelectedVendor] = useState<IVendorModel>();
    const [foundVendors, setFoundVendors] = useState<IVendorModel[]>();
    const [clearTypeahead, setClearTypeahead] = useState(false);

    useEffect(() => {
        props.searchFn(query);
    }, [query]);

    useEffect(() => {
        props.socket.on(EndpointsEnum.VendorsOnGetVendors, onGetVendors);

        return () => {
            props.socket.removeListener(EndpointsEnum.VendorsOnGetVendors, onGetVendors);
        };
    }, []);

    let cardClass = "dark-card text-white bg-secondary";

    if (UIService.isMainViewLight()) {
        cardClass = "";
    }

    const toggleVisible = () => {
        setIsVisible(!isVisible);
    };

    const handleChange = (e: ChangeEvent<any>) => {
        const value = e.target.value;
        const name = e.target.name;

        const newFormData = {
            ...formData,
            [name]: value,
        };

        setFormData(newFormData);
    };

    const getQuery = () => {
        const query: any = [];

        if (!_.isNil(selectedVendor)) {
            query.push(`vendor_guid=${selectedVendor.guid}`);
            query.push(`vendor_name=${selectedVendor.name}`);
        }

        if (!_.isNil(formData.order) && formData.order.length) {
            query.push(`order_name=${formData.order}`);
        }

        if (!_.isNil(formData.packing_list) && formData.packing_list.length) {
            query.push(`packing_list_name=${formData.packing_list}`);
        }

        if (!_.isNil(formData.pick_list) && formData.pick_list.length) {
            query.push(`pick_list_name=${formData.pick_list}`);
        }

        if (!_.isNil(formData.locked) && formData.locked) {
            query.push(`locked=${formData.locked}`);
        }

        return query;
    };

    const getQueryString = (): string => {
        const query = getQuery();

        let queryString = "";

        if (query.length) {
            queryString = "?";

            queryString += query.join("&");
        }

        if (query.length) {
            queryString = "?";

            queryString += query.join("&");
        }

        return queryString;
    };

    const search = () => {
        const queryString = getQueryString();

        history({
            pathname: "/packing_lists",
            search: queryString,
        });

        setQuery(queryString);
    };

    const reset = () => {
        setFormData(getInitialFormData());
        props.searchFn();
        setClearTypeahead(true);
    };

    const performVendorLookup = (value: string) => {
        const payload: IGetVendorsDTO = {
            userGuid: props.userModel.guid,
            userToken: props.userModel.token,
            supplierGuid: props.supplierModel.guid,
            nameGuidOnly: true,
            queryString: `name=${value}`,
        };

        props.socket.emit(EndpointsEnum.VendorsGetVendors, payload);
    };

    const onGetVendors = (data: IOnGetVendorsDTO) => {
        const vendors = data.supplierVendorTokens.map((token) => token.Vendor!);
        setFoundVendors(vendors);
    };

    const selectVendor = (vendor: IVendorModel) => {
        setSelectedVendor(vendor);
    };

    const clearTypeaheadFn = () => {
        setSelectedVendor(undefined);
        setClearTypeahead(false);
    };

    const enterSearch = (e: any) => {
        if (e.key === "Enter") {
            search();
        }
    };

    if (pick_list_name?.length) {
        const queryString = getQueryString();

        if (initSearch && query != queryString) {
            setInitSearch(false);
            setQuery(queryString);
        }
    }

    return (
        <div className={"PackingListSearch row"}>
            <div className={"row"}>
                <div className="col-2">
                    <OwLabel title={"vendor"} supplierModel={props.supplierModel} isSupplierVendorOverride={true} />
                    <Typeahead
                        isSelectableFn={() => true}
                        typeaheadApiFn={performVendorLookup}
                        title={"vendor"}
                        selectedItemFn={selectVendor}
                        selectedItem={selectedVendor}
                        typeaheadItems={foundVendors}
                        titleProp={"name"}
                        clearTypeahead={clearTypeahead}
                        clearTypeaheadCallback={clearTypeaheadFn}
                    />
                </div>

                <div className={"col-2"}>
                    <OwLabel title={"packing_list"} />
                    <OwInputWrapper name={"packing_list"} fn={handleChange} value={formData.packing_list} onKeyDownFn={enterSearch} />
                </div>

                <div className={"col-2"}>
                    <OwLabel title={"pick_list"} />
                    <OwInputWrapper name={"pick_list"} fn={handleChange} value={formData.pick_list} onKeyDownFn={enterSearch} />
                </div>

                <div className={"col-2"}>
                    <OwLabel title={"order"} />
                    <OwInputWrapper name={"order"} fn={handleChange} value={formData.order} onKeyDownFn={enterSearch} />
                </div>

                <div className={"col-2"}>
                    <OwLabel title={"status"} />
                    <div>
                        <OwSelectOption
                            name={"locked"}
                            fn={handleChange}
                            options={[
                                { value: true, title: "locked" },
                                { value: false, title: "unlocked" },
                            ]}
                            selectedOption={formData.locked}
                            titleProp={"title"}
                            valueProp={"value"}
                            translateTitle={true}
                        />
                    </div>
                </div>

                <div className={"col-2"}>
                    <OwLabel title={""} />
                    <div>
                        <OwButton className={"btn-primary"} title={"search"} fn={search} />
                        <OwButton className={"btn-danger ms-2"} title={"reset"} fn={reset} />
                    </div>
                </div>
            </div>
        </div>
    );
};
